/**
 * PRX-137 — which actions the Prescriptions row menu offers, and why one is
 * disabled. Pure, so the per-PMS, basket and permission rules are tested
 * without rendering a menu.
 *
 * Nothing here writes to the PMS, and there is no Inactivate: PHOX reads Rx
 * status from the PMS and never writes it (PRX-151). "By request only" is
 * PHOX's own `isPrn` on a subscription basket line, written through one of
 * two existing routes:
 *
 * - A line with blister settings goes through the blister PATCH, which keeps
 *   `blister.send === 'ask_first'` if and only if `isPrn` (it is the one writer of
 *   both). Turning it OFF needs a send rule that a person picks, so that
 *   direction is disabled here and points to the Blister pack tab.
 * - A line without blister settings uses the billing-queue PRN route,
 *   which writes `isPrn` alone. That route never touches `blister.send`, so it is
 *   never used for a line that has blister settings.
 */

/** One live subscription-basket line for an Rx, as the Rx tab needs it. */
export interface RxBasketLine {
  billingItemId: string;
  /** The exact position in the basket's medications — the blister route's pin. */
  medicationIndex: number;
  rxNumber: string;
  isPrn: boolean;
  /** The line carries blister settings (`blister` is set). */
  hasBlisterSettings: boolean;
}

/** What is known about the patient's baskets. `error` is never read as "no basket". */
export type RxBasketMembership =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; blisterPack: boolean; lines: RxBasketLine[] };

export interface RxRowActionsInput {
  rxNumber: string;
  /** The name the row shows (brand, else generic). Empty when it has none. */
  drugName: string;
  din: string | null;
  readOnly: boolean;
  readOnlyMessage?: string | null;
  /** The page's one `editBilling` answer — both PRN routes are billing writes. */
  canEditBilling: boolean;
  /** Specialty marking is offered on this mount and not hidden for the pharmacy. */
  specialtyAvailable: boolean;
  /** The Audit log tab is on for this pharmacy. */
  auditTrailAvailable: boolean;
  /** This Rx's basket membership (only the lines for this Rx). */
  basket: RxBasketMembership;
}

export type RxRowActionKey =
  | 'specialty'
  | 'byRequestOnly'
  | 'infoSheet'
  | 'auditTrail'
  | 'rxProfile'
  | 'billing'
  | 'images'
  | 'faxRefills'
  | 'verbalRenewal'
  | 'letter'
  | 'adapt'
  | 'vacationSupply';

export interface RxRowAction {
  key: RxRowActionKey;
  label: string;
  group: 'main' | 'forms';
  disabled: boolean;
  /** Why it is disabled — shown under the label, since a disabled item takes no hover. */
  reason?: string;
  /** For a toggle: whether it is on now. */
  checked?: boolean;
}

export type ByRequestOnlyPlan =
  | { kind: 'disabled'; reason: string }
  | { kind: 'billingPrn'; line: RxBasketLine; next: boolean }
  | { kind: 'blisterAskFirst'; line: RxBasketLine };

const READ_ONLY_FALLBACK = 'This chart is read-only';
// Staff read operational words (review L3). The dependencies stay here:
// Verbal renewal, Letter and Adapt wait on Rafik saying what each produces
// and who signs it.
const COMING_SOON = 'Coming soon';

export function byRequestOnlyPlan(input: RxRowActionsInput): ByRequestOnlyPlan {
  if (!input.canEditBilling) return { kind: 'disabled', reason: 'Needs the edit billing permission' };
  if (input.readOnly) return { kind: 'disabled', reason: input.readOnlyMessage || READ_ONLY_FALLBACK };
  const { basket } = input;
  if (basket.status === 'loading') return { kind: 'disabled', reason: 'Checking subscriptions' };
  if (basket.status === 'error') {
    return { kind: 'disabled', reason: "Couldn't check whether this Rx is on a subscription" };
  }
  if (basket.lines.length === 0) return { kind: 'disabled', reason: 'Add this Rx to a subscription first' };
  if (basket.lines.length > 1) {
    return {
      kind: 'disabled',
      reason: 'This Rx is on more than one subscription line. Change it on the subscription.',
    };
  }
  const [line] = basket.lines;
  if (line.hasBlisterSettings) {
    if (!basket.blisterPack) {
      return {
        kind: 'disabled',
        reason: 'This drug has blister pack settings and the patient is not on packs',
      };
    }
    if (line.isPrn) {
      return { kind: 'disabled', reason: 'Choose how this blister drug is sent on the Blister pack tab' };
    }
    return { kind: 'blisterAskFirst', line };
  }
  return { kind: 'billingPrn', line, next: !line.isPrn };
}

const isOn = (basket: RxBasketMembership): boolean =>
  basket.status === 'ready' && basket.lines.length === 1 && basket.lines[0].isPrn;

export function buildRxRowActions(input: RxRowActionsInput): RxRowAction[] {
  const actions: RxRowAction[] = [];

  if (input.specialtyAvailable) {
    const reason = input.readOnly
      ? input.readOnlyMessage || READ_ONLY_FALLBACK
      : !input.drugName.trim()
        ? 'This Rx has no drug name'
        : undefined;
    actions.push({ key: 'specialty', label: 'Mark as specialty', group: 'main', disabled: !!reason, reason });
  }

  const plan = byRequestOnlyPlan(input);
  actions.push({
    key: 'byRequestOnly',
    label: 'By request only',
    group: 'main',
    disabled: plan.kind === 'disabled',
    reason: plan.kind === 'disabled' ? plan.reason : undefined,
    checked: isOn(input.basket),
  });

  actions.push(
    input.din
      ? { key: 'infoSheet', label: 'Info sheet', group: 'main', disabled: false }
      : { key: 'infoSheet', label: 'Info sheet', group: 'main', disabled: true, reason: 'This Rx has no DIN' },
  );
  actions.push(
    input.auditTrailAvailable
      ? { key: 'auditTrail', label: 'Audit trail', group: 'main', disabled: false }
      : {
          key: 'auditTrail',
          label: 'Audit trail',
          group: 'main',
          disabled: true,
          reason: 'The audit log is turned off for this pharmacy',
        },
  );
  actions.push(
    { key: 'rxProfile', label: 'Rx profile', group: 'main', disabled: false },
    { key: 'billing', label: 'Billing', group: 'main', disabled: false },
    { key: 'images', label: 'Images', group: 'main', disabled: false },
  );

  actions.push(
    // Fax refills waits on fax sending (PRX-20, PRX-142).
    {
      key: 'faxRefills',
      label: 'Fax refills',
      group: 'main',
      disabled: true,
      reason: "Coming soon: fax sending isn't in PHOX yet",
    },
    { key: 'verbalRenewal', label: 'Verbal renewal', group: 'forms', disabled: true, reason: COMING_SOON },
    { key: 'letter', label: 'Letter', group: 'forms', disabled: true, reason: COMING_SOON },
    { key: 'adapt', label: 'Adapt', group: 'forms', disabled: true, reason: COMING_SOON },
    // PRX-130: opens the vacation supply calculator with this Rx ticked. It
    // only reads; its one write (a request task) is gated in the view.
    { key: 'vacationSupply', label: 'Vacation supply', group: 'forms', disabled: false },
  );

  return actions;
}
